import { useState } from 'react'
import { Box, Button, Chip, Tooltip, Alert } from '@mui/material'
import { Lock, LockOpen, TaskAlt } from '@mui/icons-material'
import { useApi } from '../../hooks/useApi'
import { useAuth } from '../../hooks/useAuth'
import { formatDate } from '../../utils/format'

// FAR-61: approve / unapprove a Production Plan from the Planning Board header.
// Mark: "On the main plan view/edit screen. Approved by the planner. Approval
// should stamp date/time and user id." "Mark as Production Plan" is now the
// same thing as Approved.

export const APPROVED = 'Approved' // must match models.PlanStatusApproved
const APPROVAL_ROLES = ['planner', 'approver', 'admin'] // must match handlers.approvalRoles

export function canApprove(user) {
  const roles = String(user?.roles || '').split(',').map((r) => r.trim())
  return roles.some((r) => APPROVAL_ROLES.includes(r))
}

export default function ApprovalControl({ plan, plantCode, onChange }) {
  const { post } = useApi()
  const { user } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  if (!plan?.id) return null
  const approved = plan.status === APPROVED
  const allowed = canApprove(user)

  const act = async (action) => {
    setBusy(true)
    setError(null)
    try {
      const updated = await post(`/plans/${plan.id}/${action}`, { plant: plantCode })
      onChange?.(updated)
    } catch (e) {
      setError(e.message || `Could not ${action} the plan`)
    } finally {
      setBusy(false)
    }
  }

  const stamp = approved && plan.approvedAt
    ? `Approved ${formatDate(plan.approvedAt)}${plan.approvedBy ? ` by ${plan.approvedBy}` : ''}`
    : null

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {approved && (
        <Tooltip title={stamp || 'Approved'}>
          <Chip icon={<Lock sx={{ fontSize: 16 }} />} label="Approved" color="primary" size="small" />
        </Tooltip>
      )}
      <Tooltip title={allowed ? '' : 'Only planners, approvers and admins can approve'}>
        <span>
          {approved ? (
            <Button size="small" variant="outlined" startIcon={<LockOpen />} disabled={!allowed || busy} onClick={() => act('unapprove')}>
              Unapprove
            </Button>
          ) : (
            <Button size="small" variant="contained" startIcon={<TaskAlt />} disabled={!allowed || busy} onClick={() => act('approve')}>
              Approve
            </Button>
          )}
        </span>
      </Tooltip>
      {error && <Alert severity="error" sx={{ py: 0 }} onClose={() => setError(null)}>{error}</Alert>}
    </Box>
  )
}
